import React, { Suspense } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from '@/components/ui/sheet';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useQuickPeek } from '@/contexts/QuickPeekContext';
import { asBillId, asLegislatorId } from '@/types/v2';

const BillPeekContent = React.lazy(() => import('@/components/bills/BillPeekContent'));
const LegislatorPeekContent = React.lazy(
  () => import('@/components/legislators/LegislatorPeekContent')
);
const CommitteePeekContent = React.lazy(
  () => import('@/components/committees/CommitteePeekContent')
);

const ENTITY_LABELS: Record<string, string> = {
  bill: 'Bill',
  legislator: 'Legislator',
  committee: 'Committee',
};

function PeekSkeleton() {
  return (
    <div className="space-y-4 p-1">
      <Skeleton className="h-6 w-3/4" />
      <Skeleton className="h-4 w-1/2" />
      <Skeleton className="h-20 w-full" />
      <Skeleton className="h-4 w-2/3" />
      <Skeleton className="h-16 w-full" />
    </div>
  );
}

function useFullDetailsHref(entityType: string, entityId: number): string {
  // Fall back to ID-based routes since we don't have the slug info at panel level.
  switch (entityType) {
    case 'bill':
      // Can't build SEO URL without bill number — use peek content's internal link
      return `/bills/${entityId}`;
    case 'legislator':
      return `/legislators/${entityId}`;
    case 'committee':
      return `/committees/${entityId}`;
    default:
      return '#';
  }
}

export function QuickPeekPanel() {
  const { peekState, closePeek } = useQuickPeek();
  const isOpen = peekState !== null;

  const handleOpenChange = (open: boolean) => {
    if (!open) closePeek();
  };

  return (
    <Sheet open={isOpen} onOpenChange={handleOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md flex flex-col p-0">
        {peekState && (
          <PeekPanelContent
            entityType={peekState.entityType}
            entityId={peekState.entityId}
            onClose={closePeek}
          />
        )}
      </SheetContent>
    </Sheet>
  );
}

function PeekPanelContent({
  entityType,
  entityId,
  onClose,
}: {
  entityType: string;
  entityId: number;
  onClose: () => void;
}) {
  return (
    <>
      <SheetHeader className="px-6 pt-6 pb-2">
        <SheetTitle>{ENTITY_LABELS[entityType] ?? 'Details'}</SheetTitle>
        <SheetDescription className="sr-only">
          Quick preview of {entityType} details
        </SheetDescription>
      </SheetHeader>

      <ScrollArea className="flex-1 px-6">
        <Suspense fallback={<PeekSkeleton />}>
          {entityType === 'bill' && <BillPeekContent billId={asBillId(entityId)} />}
          {entityType === 'legislator' && (
            <LegislatorPeekContent legislatorId={asLegislatorId(entityId)} />
          )}
          {entityType === 'committee' && <CommitteePeekContent committeeId={entityId} />}
        </Suspense>
      </ScrollArea>

      <SheetFooter className="px-6 py-4 border-t">
        <ViewFullDetailsButton entityType={entityType} entityId={entityId} onClose={onClose} />
      </SheetFooter>
    </>
  );
}

function ViewFullDetailsButton({
  entityType,
  entityId,
  onClose,
}: {
  entityType: string;
  entityId: number;
  onClose: () => void;
}) {
  const href = useFullDetailsHref(entityType, entityId);

  return (
    <Button asChild variant="outline" className="w-full" onClick={onClose}>
      <Link to={href}>
        <ExternalLink className="h-4 w-4 mr-2" />
        View Full Details
      </Link>
    </Button>
  );
}
